import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, serverTimestamp, writeBatch, Timestamp, deleteField, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { Product, NewProduct, ProductFilter, ProductDisplayIngredient } from '../models/Product';
import imageOptimizationService from './imageOptimizationService';
import { adminScopeService } from './adminScopeService';

// Collection Firestore
const PRODUCTS_COLLECTION = 'products';

const normalizeDisplayIngredientLabel = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const parseDisplayIngredients = (data: any): ProductDisplayIngredient[] => {
  const raw = Array.isArray(data?.displayIngredients)
    ? data.displayIngredients
    : Array.isArray(data?.menuIngredients)
      ? data.menuIngredients
      : [];

  if (raw.length > 0) {
    return raw
      .map((item: any, index: number): ProductDisplayIngredient | null => {
        if (typeof item === 'string') {
          const label = item.trim();
          if (!label) return null;
          return {
            menuIngredientId: normalizeDisplayIngredientLabel(label) || `ingredient_${index + 1}`,
            label,
            removable: true,
            isDefaultIncluded: true,
            sortOrder: index
          };
        }

        if (!item || typeof item !== 'object') return null;
        const label = String(item.label ?? item.name ?? item.displayName ?? '').trim();
        if (!label) return null;
        return {
          menuIngredientId: String(
            item.menuIngredientId ??
            item.id ??
            item.ingredientId ??
            normalizeDisplayIngredientLabel(label) ??
            `ingredient_${index + 1}`
          ),
          label,
          removable: item.removable !== false,
          isDefaultIncluded: item.isDefaultIncluded !== false,
          sortOrder: typeof item.sortOrder === 'number' ? item.sortOrder : index
        };
      })
      .filter((item: ProductDisplayIngredient | null): item is ProductDisplayIngredient => item !== null)
      .sort((a: ProductDisplayIngredient, b: ProductDisplayIngredient) => a.sortOrder - b.sortOrder);
  }

  if (Array.isArray(data?.ingredients)) {
    return data.ingredients
      .map((item: any, index: number): ProductDisplayIngredient | null => {
        const label = String(item ?? '').trim();
        if (!label) return null;
        return {
          menuIngredientId: normalizeDisplayIngredientLabel(label) || `ingredient_${index + 1}`,
          label,
          removable: true,
          isDefaultIncluded: true,
          sortOrder: index
        };
      })
      .filter((item: ProductDisplayIngredient | null): item is ProductDisplayIngredient => item !== null);
  }

  return [];
};

const buildDisplayIngredientsForSave = (
  ingredients: unknown,
  storeId: string,
  existingDisplayIngredients?: ProductDisplayIngredient[]
): ProductDisplayIngredient[] => {
  if (!Array.isArray(ingredients)) {
    return [];
  }

  const existingByLabel = new Map<string, ProductDisplayIngredient>();
  (existingDisplayIngredients ?? []).forEach((ingredient) => {
    existingByLabel.set(normalizeDisplayIngredientLabel(ingredient.label), ingredient);
  });

  const seen = new Set<string>();
  const result: ProductDisplayIngredient[] = [];

  ingredients.forEach((item, index) => {
    const label = String(item ?? '').trim();
    if (!label) return;
    const normalized = normalizeDisplayIngredientLabel(label);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);

    const previous = existingByLabel.get(normalized);
    result.push({
      menuIngredientId: previous?.menuIngredientId || `${storeId}__${normalized}`,
      label,
      removable: previous?.removable ?? true,
      isDefaultIncluded: previous?.isDefaultIncluded ?? true,
      sortOrder: index
    });
  });

  return result;
};

const normalizeDisplayIngredientsForSave = (
  displayIngredients: unknown,
  storeId: string
): ProductDisplayIngredient[] | null => {
  if (!Array.isArray(displayIngredients)) {
    return null;
  }

  const seen = new Set<string>();
  return displayIngredients
    .map((item: any, index): ProductDisplayIngredient | null => {
      const label = String(item?.label ?? '').trim();
      if (!label) return null;
      const normalized = normalizeDisplayIngredientLabel(label);
      const id = String(item?.menuIngredientId ?? '').trim() || `${storeId}__${normalized || `ingredient_${index + 1}`}`;
      const key = `${id}:${normalized}`;
      if (seen.has(key)) return null;
      seen.add(key);
      return {
        menuIngredientId: id,
        label,
        removable: item?.removable !== false,
        isDefaultIncluded: item?.isDefaultIncluded !== false,
        sortOrder: typeof item?.sortOrder === 'number' ? item.sortOrder : index
      };
    })
    .filter((item): item is ProductDisplayIngredient => item !== null)
    .map((item, index) => ({ ...item, sortOrder: index }));
};

const normalizeCompositionMode = (value: unknown, product?: { ingredients?: string[]; displayIngredients?: ProductDisplayIngredient[] }): 'SIMPLE' | 'COMPOSITE' => {
  if (value === 'SIMPLE' || value === 'COMPOSITE') return value;
  return (product?.displayIngredients?.length || product?.ingredients?.length) ? 'COMPOSITE' : 'SIMPLE';
};

// Conversion des données Firestore en modèle Product
const convertFirestoreDataToProduct = (id: string, data: any): Product => {
  const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined;
  const updatedAt = data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined;
  
  // Le champ category contient directement le nom de la catégorie
  const categoryValue = data.categoryId || data.category || '';
  const isActive = data.isActive ?? data.isAvailable ?? true;
  const isVisibleOnline = data.isVisibleOnline ?? data.isAvailable ?? true;
  const isVisiblePos = data.isVisiblePos ?? true;
  // Reflect the stored Firestore value when present.
  // Some legacy or transitional docs may be inconsistent, and the admin must
  // show the real persisted availability instead of recomputing it silently.
  const isAvailable =
    typeof data.isAvailable === 'boolean'
      ? data.isAvailable
      : (isActive && isVisibleOnline);
  
    return {
      id,
      name: data.name || '[Sans nom]',
      category: categoryValue,
      categoryId: data.categoryId,
      categoryKey: data.categoryKey,
      storeId: data.storeId,
      catalogProductKey: data.catalogProductKey,
      sourceStoreId: data.sourceStoreId,
      sourceProductId: data.sourceProductId,
      price: typeof data.price === 'number' ? data.price : 0,
      priceXL: typeof data.priceXL === 'number' ? data.priceXL : undefined,
      description: data.description || '',
      ingredients: Array.isArray(data.ingredients) ? data.ingredients : [],
      displayIngredients: parseDisplayIngredients(data),
      compositionMode: normalizeCompositionMode(data.compositionMode, {
        ingredients: Array.isArray(data.ingredients) ? data.ingredients : [],
        displayIngredients: parseDisplayIngredients(data),
      }),
      imageUrl: data.imageUrl || null,
      // Images optimisées pour l'app client
      thumbnailUrl: data.thumbnailUrl || null,
      mediumImageUrl: data.mediumImageUrl || null,
      highImageUrl: data.highImageUrl || null,
      isAvailable,
      isActive,
      isVisibleOnline,
      isVisiblePos,
      isFeatured: data.isFeatured === true,
      order: data.order || 0,
      categoryOrder: typeof data.displayOrder === 'number'
        ? data.displayOrder
        : (typeof data.categoryOrder === 'number' ? data.categoryOrder : undefined),
      customizationSections: Array.isArray(data.customizationSections) ? data.customizationSections : [],
      tags: Array.isArray(data.tags) ? data.tags : [],
      usesCategoryRules: data.usesCategoryRules === true,
      isOnPromotion: data.isOnPromotion === true, // Map the new field
      discountPrice: typeof data.discountPrice === 'number' ? data.discountPrice : undefined, // Map the new field
      createdAt,
      updatedAt
    };
};

const productMatchesCurrentStoreScope = (product: Product): boolean => {
  const currentStoreId = adminScopeService.getCurrentStoreId();
  if (!currentStoreId) {
    return true;
  }
  return product.storeId === currentStoreId;
};

const assertProductInCurrentStoreScope = (
  product: Product | null,
  id: string,
  action: string
): Product => {
  if (!product) {
    throw new Error(`Produit ${id} introuvable pour ${action}.`);
  }

  if (!productMatchesCurrentStoreScope(product)) {
    throw new Error(`Le produit ${id} est hors du scope magasin actif.`);
  }

  return product;
};

// Service pour la gestion des produits
const productService = {
  // Récupérer tous les produits
  getProducts: async (filter?: ProductFilter): Promise<Product[]> => {
    try {
      const hasExplicitStoreFilter = filter ? Object.prototype.hasOwnProperty.call(filter, 'storeId') : false;
      const currentStoreId = hasExplicitStoreFilter ? filter?.storeId ?? null : adminScopeService.getCurrentStoreId();
      const productsRef = collection(db, PRODUCTS_COLLECTION);
      const querySnapshot = currentStoreId
        ? await getDocs(query(productsRef, where('storeId', '==', currentStoreId)))
        : await getDocs(productsRef);
      let products = querySnapshot.docs.map(doc =>
        convertFirestoreDataToProduct(doc.id, doc.data())
      );

      if (currentStoreId) {
        products = products.filter((product) => product.storeId === currentStoreId);
      }

      if (filter?.category && !filter?.isOnPromotionOnly) {
        const desired = filter.category;
        products = products.filter(p =>
          p.category === desired ||
          p.category.toLowerCase() === desired.toLowerCase()
        );
      }
      if (filter?.searchTerm) {
        const term = filter.searchTerm.toLowerCase();
        products = products.filter(p =>
          p.name.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term) ||
          p.ingredients?.some(ing => ing.toLowerCase().includes(term)) ||
          p.tags?.some(tag => tag.toLowerCase().includes(term))
        );
      }
      if (filter?.availableOnly) {
        products = products.filter(p => p.isAvailable);
      }
      if (filter?.featuredOnly) {
        products = products.filter(p => p.isFeatured);
      }
      if (filter?.tagFilter) {
        const tag = filter.tagFilter;
        products = products.filter(p => p.tags?.includes(tag));
      }
      if (filter?.isOnPromotionOnly) {
        products = products.filter(p => p.isOnPromotion === true);
      }
      
      if (filter?.category) {
        products.sort((a, b) => {
          const orderA = a.categoryOrder ?? 0;
          const orderB = b.categoryOrder ?? 0;
          return orderA - orderB;
        });
      } else {
        products.sort((a, b) => {
          const orderA = a.order ?? 0;
          const orderB = b.order ?? 0;
          return orderA - orderB;
        });
      }
      
      return products;
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
      throw error;
    }
  },
  
  // Récupérer un produit par son ID
  getProductById: async (id: string): Promise<Product | null> => {
    try {
      const docRef = doc(db, PRODUCTS_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const product = convertFirestoreDataToProduct(docSnap.id, docSnap.data());
      if (!productMatchesCurrentStoreScope(product)) {
        return null;
      }

      return product;
    } catch (error) {
      console.error(`Erreur lors de la récupération du produit ${id}:`, error);
      throw error;
    }
  },
  
  // Créer un nouveau produit
  createProduct: async (product: NewProduct, imageFile?: File): Promise<Product> => {
    try {
      let order = 0;
      let categoryOrder = 0;
      try {
        const allProducts = await productService.getProducts();
        if (allProducts.length > 0) {
          const maxOrder = Math.max(...allProducts.map(p => p.order || 0));
          order = maxOrder + 1;
        }
        
        const existingProductsInCategory = await productService.getProducts({ category: product.category });
        if (existingProductsInCategory.length > 0) {
          const maxCategoryOrder = Math.max(...existingProductsInCategory.map(p => p.categoryOrder || 0));
          categoryOrder = maxCategoryOrder + 1;
        }
      } catch (orderError) {
        console.error('Erreur lors de la détermination de l\'ordre du produit:', orderError);
      }

      const storeId = adminScopeService.getCurrentStoreId();
      if (!storeId) {
        throw new Error('Aucun magasin actif sélectionné pour créer un produit.');
      }

      const categoryId = typeof product.category === 'string' ? product.category.trim() : '';
      if (!categoryId) {
        throw new Error('La catégorie est requise et ne peut pas être vide');
      }

      const isActive = product.isActive ?? product.isAvailable ?? true;
      const isVisibleOnline = product.isVisibleOnline ?? product.isAvailable ?? true;
      const isVisiblePos = product.isVisiblePos ?? true;
      const compositionMode = normalizeCompositionMode(product.compositionMode, product);
      const productIngredients = compositionMode === 'SIMPLE' ? [] : (product.ingredients ?? []);
      const productDisplayIngredients = compositionMode === 'SIMPLE'
        ? []
        : (normalizeDisplayIngredientsForSave(product.displayIngredients, storeId) ??
          buildDisplayIngredientsForSave(product.ingredients, storeId));

      const newProductData: any = {
        name: product.name,
        category: categoryId.toUpperCase(),
        categoryId,
        categoryKey: categoryId,
        storeId,
        price: product.price,
        priceXL: product.priceXL,
        description: product.description ?? '',
        compositionMode,
        ingredients: productIngredients,
        displayIngredients: productDisplayIngredients,
        isAvailable: isActive && isVisibleOnline,
        isActive,
        isVisibleOnline,
        isVisiblePos,
        isFeatured: product.isFeatured,
        tags: product.tags,
        customizationSections: Array.isArray(product.customizationSections) ? product.customizationSections : [],
        usesCategoryRules: product.usesCategoryRules === true,
        isOnPromotion: product.isOnPromotion ?? false,
        discountPrice: product.discountPrice,
        order,
        categoryOrder,
        displayOrder: categoryOrder,
        catalogProductKey: product.catalogProductKey,
        sourceStoreId: product.sourceStoreId ?? storeId,
        sourceProductId: product.sourceProductId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (typeof product.imageUrl === "string" && product.imageUrl) {
        newProductData.imageUrl = product.imageUrl;
      }

      Object.keys(newProductData).forEach(key => {
        if (newProductData[key] === undefined) {
          delete newProductData[key];
        }
      });
      if (!newProductData.catalogProductKey) {
        delete newProductData.catalogProductKey;
      }

      const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), newProductData);

      await updateDoc(docRef, {
        catalogProductKey: docRef.id,
        sourceProductId: docRef.id,
      });
      newProductData.catalogProductKey = docRef.id;
      newProductData.sourceProductId = docRef.id;

      if (imageFile) {
        const imageUrl = await productService.uploadProductImage(docRef.id, imageFile, product.name);
        await updateDoc(docRef, { imageUrl });
        newProductData.imageUrl = imageUrl;
      }

      return {
        id: docRef.id,
        ...newProductData,
        createdAt: new Date(),
        updatedAt: new Date()
      } as Product;
    } catch (error) {
      console.error('Erreur lors de la création du produit:', error);
      throw error;
    }
  },
  
  // Mettre à jour un produit existant
  updateProduct: async (id: string, productData: Partial<Product>, imageFile?: File): Promise<Product> => {
    try {
      const productRef = doc(db, PRODUCTS_COLLECTION, id);
      const currentProduct = assertProductInCurrentStoreScope(
        await productService.getProductById(id),
        id,
        'mise à jour'
      );
      
      const updateData: any = {
        ...productData,
        updatedAt: serverTimestamp()
      };
      
      if ('category' in updateData) {
        if (typeof updateData.category !== 'string' || !updateData.category.trim()) {
          delete updateData.category;
        } else {
          const nextCategoryId = updateData.category.trim();
          updateData.category = nextCategoryId.toUpperCase();
          updateData.categoryId = nextCategoryId;
          updateData.categoryKey = nextCategoryId;
        }
      }

      if ('isActive' in updateData || 'isVisibleOnline' in updateData || 'isAvailable' in updateData) {
        const hasExplicitIsActive = Object.prototype.hasOwnProperty.call(updateData, 'isActive');
        const hasExplicitIsVisibleOnline = Object.prototype.hasOwnProperty.call(updateData, 'isVisibleOnline');
        const hasExplicitIsAvailable = Object.prototype.hasOwnProperty.call(updateData, 'isAvailable');

        const isActive = hasExplicitIsActive
          ? updateData.isActive
          : currentProduct?.isActive ?? true;

        const isVisibleOnline = hasExplicitIsVisibleOnline
          ? updateData.isVisibleOnline
          : currentProduct?.isVisibleOnline ?? true;

        const derivedAvailability = Boolean(isActive && isVisibleOnline);
        const isAvailable =
          hasExplicitIsAvailable && !hasExplicitIsActive && !hasExplicitIsVisibleOnline
            ? Boolean(updateData.isAvailable)
            : derivedAvailability;

        updateData.isActive = isActive;
        updateData.isVisibleOnline = isVisibleOnline;
        updateData.isAvailable = isAvailable;
      }

      if ('categoryOrder' in updateData && typeof updateData.categoryOrder === 'number') {
        updateData.displayOrder = updateData.categoryOrder;
      }

      const nextCompositionMode = normalizeCompositionMode(
        updateData.compositionMode ?? currentProduct.compositionMode,
        {
          ingredients: updateData.ingredients ?? currentProduct.ingredients,
          displayIngredients: updateData.displayIngredients ?? currentProduct.displayIngredients,
        }
      );
      updateData.compositionMode = nextCompositionMode;

      if (nextCompositionMode === 'SIMPLE') {
        updateData.ingredients = [];
        updateData.displayIngredients = [];
      } else if ('displayIngredients' in updateData) {
        const normalizedDisplayIngredients = normalizeDisplayIngredientsForSave(
          updateData.displayIngredients,
          currentProduct.storeId || adminScopeService.getCurrentStoreId() || 'default_store'
        );
        updateData.displayIngredients = normalizedDisplayIngredients ?? [];
        updateData.ingredients = updateData.displayIngredients.map((item: ProductDisplayIngredient) => item.label);
      } else if ('ingredients' in updateData) {
        updateData.displayIngredients = buildDisplayIngredientsForSave(
          updateData.ingredients,
          currentProduct.storeId || adminScopeService.getCurrentStoreId() || 'default_store',
          currentProduct.displayIngredients
        );
      }

      delete updateData.id;

      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined && key !== 'description') {
          delete updateData[key];
        }
      });

      if ('priceXL' in updateData && updateData.priceXL === null) {
        updateData.priceXL = deleteField();
      }

      if (imageFile) {
        const productName = updateData.name || currentProduct?.name || 'Produit';
        const imageUrl = await productService.uploadProductImage(id, imageFile, productName);
        updateData.imageUrl = imageUrl;
      }

      await updateDoc(productRef, updateData);

      const updatedProduct = await productService.getProductById(id);
      if (!updatedProduct) {
        throw new Error(`Produit ${id} non trouvé après mise à jour`);
      }
      
      return updatedProduct;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du produit ${id}:`, error);
      throw error;
    }
  },
  
  // Supprimer un produit
  deleteProduct: async (id: string): Promise<void> => {
    try {
      // 1. Récupérer le produit pour vérifier s'il y a une image à supprimer
      const product = assertProductInCurrentStoreScope(
        await productService.getProductById(id),
        id,
        'suppression'
      );
      
      // 2. Supprimer le produit de Firestore
      await deleteDoc(doc(db, PRODUCTS_COLLECTION, id));
      
      // 3. Si le produit avait une image, la supprimer du stockage
      if (product?.imageUrl) {
        try {
          // Extraire le nom du fichier de l'URL
          const imageRef = ref(storage, `products/${id}`);
          await deleteObject(imageRef);
        } catch (imageError) {
          console.error(`Erreur lors de la suppression de l'image du produit ${id}:`, imageError);
          // On continue même si la suppression de l'image échoue
        }
      }
    } catch (error) {
      console.error(`Erreur lors de la suppression du produit ${id}:`, error);
      throw error;
    }
  },
  
  // Télécharger une image de produit avec optimisation automatique
  uploadProductImage: async (productId: string, imageFile: File, productName?: string): Promise<string> => {
    try {
      console.log(`🚀 Upload image produit ${productId} avec optimisation...`);
      
      // 1. Upload de l'image originale (pour compatibilité)
      const storageRef = ref(storage, `products/${productId}`);
      await uploadBytes(storageRef, imageFile);
      const originalImageUrl = await getDownloadURL(storageRef);
      
      // 2. Générer les versions optimisées si le nom du produit est fourni
      if (productName) {
        try {
          console.log(`🔧 Génération des versions optimisées pour: ${productName}`);
          
          const optimizedUrls = await imageOptimizationService.optimizeProductImage(
            productId,
            imageFile,
            productName,
            { quality: 0.8, format: 'webp' }
          );
          
          // 3. Mettre à jour Firestore avec les URLs optimisées
          const productRef = doc(db, PRODUCTS_COLLECTION, productId);
          await updateDoc(productRef, {
            thumbnailUrl: optimizedUrls.thumbnailUrl,
            mediumImageUrl: optimizedUrls.mediumImageUrl,
            highImageUrl: optimizedUrls.highImageUrl,
            optimizedAt: serverTimestamp()
          });
          
          console.log(`✅ Optimisation terminée pour: ${productName}`);
          console.log(`📐 URLs générées:`, {
            thumbnail: optimizedUrls.thumbnailUrl.length + ' chars',
            medium: optimizedUrls.mediumImageUrl.length + ' chars',
            high: optimizedUrls.highImageUrl.length + ' chars'
          });
          
        } catch (optimizationError) {
          console.warn(`⚠️ Erreur optimisation pour ${productName}:`, optimizationError);
          // Continuer avec l'image originale en cas d'erreur d'optimisation
        }
      }
      
      return originalImageUrl;
    } catch (error) {
      console.error(`Erreur lors du téléchargement de l'image pour le produit ${productId}:`, error);
      throw error;
    }
  },
  
  // Mettre à jour l'ordre d'affichage d'un produit
  updateProductOrder: async (id: string, newOrder: number): Promise<void> => {
    try {
      const productRef = doc(db, PRODUCTS_COLLECTION, id);
      await updateDoc(productRef, {
        order: newOrder,
        displayOrder: newOrder,
        updatedAt: serverTimestamp()
      });
      console.log(`Ordre du produit ${id} mis à jour: ${newOrder}`);
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'ordre du produit ${id}:`, error);
      throw error;
    }
  },
  
  // Réorganiser l'ordre des produits après un glisser-déposer (ordre global)
  reorderProducts: async (reorderedProducts: {id: string, order: number}[]): Promise<void> => {
    try {
      // Utiliser une transaction batch pour mettre à jour toutes les catégories en une seule opération
      const batch = writeBatch(db);
      
      reorderedProducts.forEach(({id, order}) => {
        const productRef = doc(db, PRODUCTS_COLLECTION, id);
        batch.update(productRef, {
          order,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      console.log(`Réorganisation de ${reorderedProducts.length} produits terminée`);
    } catch (error) {
      console.error('Erreur lors de la réorganisation des produits:', error);
      throw error;
    }
  },
  
  // Réorganiser l'ordre des produits dans une catégorie spécifique
  reorderProductsInCategory: async (reorderedProducts: {id: string, categoryOrder: number}[]): Promise<void> => {
    try {
      // Utiliser une transaction batch pour mettre à jour l'ordre des produits dans la catégorie
      const batch = writeBatch(db);
      
      reorderedProducts.forEach(({id, categoryOrder}) => {
        const productRef = doc(db, PRODUCTS_COLLECTION, id);
        batch.update(productRef, {
          categoryOrder,
          displayOrder: categoryOrder,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      console.log(`Réorganisation de ${reorderedProducts.length} produits dans la catégorie terminée`);
    } catch (error) {
      console.error('Erreur lors de la réorganisation des produits dans la catégorie:', error);
      throw error;
    }
  },
  
  // Mettre à jour la mise en avant d'un produit
  toggleProductFeatured: async (id: string, isFeatured: boolean): Promise<void> => {
    try {
      const productRef = doc(db, PRODUCTS_COLLECTION, id);
      await updateDoc(productRef, {
        isFeatured,
        updatedAt: serverTimestamp()
      });
      console.log(`Mise en avant du produit ${id} définie sur: ${isFeatured}`);
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la mise en avant du produit ${id}:`, error);
      throw error;
    }
  },
  
  // Récupérer le nombre de produits
  getProductCount: async (filter?: ProductFilter): Promise<number> => {
    try {
      // Pour plus d'efficacité, nous utilisons la fonction getProducts et comptons les résultats
      const products = await productService.getProducts(filter);
      return products.length;
    } catch (error) {
      console.error('Erreur lors du comptage des produits:', error);
      throw error;
    }
  },
  
  // Récupérer le nombre de règles de customisation
  getCustomizationRulesCount: async (): Promise<number> => {
    try {
      // Compter les produits qui ont des règles de personnalisation
      const products = await productService.getProducts();
      const productsWithCustomizations = products.filter(
        product => product.customizationSections && product.customizationSections.length > 0
      );
      return productsWithCustomizations.length;
    } catch (error) {
      console.error('Erreur lors du comptage des règles de customisation:', error);
      throw error;
    }
  },

  // Mettre à jour la catégorie de tous les produits qui utilisent une catégorie spécifique
  updateProductsCategory: async (oldCategoryName: string, newCategoryName: string): Promise<void> => {
    try {
      console.log(`Mise à jour des produits: ${oldCategoryName} -> ${newCategoryName}`);
      
      // 1. Récupérer tous les produits de l'ancienne catégorie
      const productsToUpdate = await productService.getProducts({ category: oldCategoryName });
      
      if (productsToUpdate.length === 0) {
        console.log(`Aucun produit trouvé pour la catégorie: ${oldCategoryName}`);
        return;
      }
      
      console.log(`${productsToUpdate.length} produits trouvés à mettre à jour`);
      
      // 2. Utiliser une transaction batch pour mettre à jour tous les produits
      const batch = writeBatch(db);
      
      productsToUpdate.forEach(product => {
        const productRef = doc(db, PRODUCTS_COLLECTION, product.id);
        batch.update(productRef, {
          category: newCategoryName.toUpperCase(),
          isAvailable: (product.isActive ?? product.isAvailable ?? true) && (product.isVisibleOnline ?? product.isAvailable ?? true),
          updatedAt: serverTimestamp()
        });
      });
      
      // 3. Exécuter la transaction
      await batch.commit();
      
      console.log(`Mise à jour terminée: ${productsToUpdate.length} produits mis à jour avec la nouvelle catégorie: ${newCategoryName}`);
    } catch (error) {
      console.error('Erreur lors de la mise à jour des produits avec la nouvelle catégorie:', error);
      throw error;
    }
  }
};

export default productService;
